<template>
  <SloLoader :loading="paymentDetailsLoading" size="xl">
    <article class="personal-details">
      <div class="p-md sm:p-lg">
        <h3 class="text-xl fw-strong">{{ 'Saved Payment Methods' | t }}</h3>
      </div>
      <transition name="fade-up">
        <SloModal
          v-if="showRemovePaymentDetailModal"
          :max-width="600"
          :form="false"
          @dismiss="handleCancelRemovePaymentDetail"
        >
          <template v-slot:title>{{ 'Remove Payment Method' | t }}</template>
          <p>{{ 'Are you sure you want to remove this payment method?' | t }}</p>
          <template v-slot:footer>
            <SloButton
              :disabled="paymentDetailsLoading"
              @click="handleRemovePaymentDetail"
              fill="outline"
            >
              <SloLoader :loading="paymentDetailsLoading" size="md">
                {{ 'Yes, remove this payment method' | t }}
              </SloLoader>
            </SloButton>
            <SloButton
              :disabled="paymentDetailsLoading"
              @click="handleCancelRemovePaymentDetail"
            >
              {{ 'No, keep this payment method' | t }}
            </SloButton>
          </template>
        </SloModal>
      </transition>
      <ul class="payment-details--saved-payment-methods">
        <li
          v-for="(paymentDetail, index) in paymentDetails"
          :key="`payment-detail=${index}`"
          :class="{
            'payment-details--saved-payment-method': true,
            'checked': paymentDetail?.defaultPayment
          }"
        >
          <div class="flex items-start gap-md">
            <SloIcon
              :name="paymentDetail?.cardType.name | getCardTypeIconName"
              size="lg"
              class="card-icon"
            />
            <div class="saved-payment-method--details">
              <span class="saved-payment-method--title">
                <span class="text-capitalize">{{ paymentDetail?.cardType.code }}</span>
                {{ paymentDetail.cardNumber | getCardNumberLastFourDigits }}
                <span v-if="paymentDetail?.defaultPayment">({{ 'default' | t }})</span>
              </span>
              <span :class="{'card-is-expired c-red-400': isCardExpired(paymentDetail)}">
                {{ 'Expires' | t }}: {{ paymentDetail?.expiryMonth }}/{{ paymentDetail?.expiryYear }}
                <span v-if="isCardExpired(paymentDetail)">{{ '(expired)' | t }}</span>
              </span>
              <span>{{ paymentDetail?.billingAddress | formatSingleLineAddress }}</span>
              <div class="hide items-center flex-wrap gap-md mt-md sm:flex">
                <SloButton
                  @click="handlePaymentDetailUpdate(paymentDetail.id)"
                  fill="clear"
                  size="small"
                  class="p-0"
                >
                  {{ 'Update' | t }}
                </SloButton>
                <SloButton
                  @click="openRemovePaymentDetailModal(paymentDetail.id)"
                  fill="clear"
                  size="small"
                  class="p-0"
                >
                  {{ 'Remove' | t }}
                </SloButton>
                <SloButton
                  v-if="!paymentDetail.defaultPayment"
                  @click="handleMakePaymentDefault(paymentDetail)"
                  fill="outline"
                  size="small"
                  class="self-start"
                >
                  {{ 'Make Default' | t }}
                </SloButton>
              </div>
            </div>
          </div>
          <div class="flex items-center flex-wrap gap-md mt-md sm:hide">
            <SloButton
              @click="handlePaymentDetailUpdate(paymentDetail.id)"
              fill="clear"
              size="small"
              class="p-0"
            >
              {{ 'Update' | t }}
            </SloButton>
            <SloButton
              @click="openRemovePaymentDetailModal(paymentDetail.id)"
              fill="clear"
              size="small"
              class="p-0"
            >
              {{ 'Remove' | t }}
            </SloButton>
            <SloButton
              v-if="!paymentDetail.defaultPayment"
              @click="handleMakePaymentDefault(paymentDetail)"
              fill="outline"
              size="small"
              class="self-start"
            >
              {{ 'Make Default' | t }}
            </SloButton>
          </div>
          <SloIcon name="CircleCheckSolid" />
        </li>
      </ul>
      <div class="personal-details--footer flex justify-end px-md py-md bg-gray-100 sm:px-lg">
        <SloButton :to="{name: 'marketplace-account-payment-add'}" fill="solid">
          {{ 'Add New Payment Method' | t }}
        </SloButton>
      </div>
    </article>
  </SloLoader>
</template>

<script>
import t from '../../main/i18n';
import {marketplaceApi} from '../apis/marketplace';
import {
  getCardNumberLastFourDigits,
  getCardTypeIconName,
  formatSingleLineAddress,
  isCardExpired
} from '../../main/utils';
import SloButton from '../../../lib/components/SloButton.vue';
import SloLoader from '../../../lib/components/SloLoader.vue';
import SloIcon from '../../../lib/components/SloIcon.vue';
import SloField from '../../../lib/components/SloField.vue';
import SloLabel from '../../../lib/components/SloLabel.vue';
import SloInput from '../../../lib/components/SloInput.vue';
import SloSelect from '../../../lib/components/SloSelect.vue';
import SloModal from '../../../lib/components/SloModal.vue';

export default {
  components: {
    SloButton,
    SloLoader,
    SloIcon,
    SloField,
    SloLabel,
    SloInput,
    SloSelect,
    SloModal
  },
  filters: {
    t,
    getCardNumberLastFourDigits,
    getCardTypeIconName,
    formatSingleLineAddress
  },
  data() {
    return {
      paymentDetailsLoading: true,
      paymentDetails: [],
      showRemovePaymentDetailModal: false,
      paymentDetailIdToRemove: null,
      paymentDetail: null
    };
  },
  async mounted() {
    await this.getPaymentDetails();
  },
  methods: {
    async getPaymentDetails() {
      await marketplaceApi.getCurrentUserPaymentDetails()
        .then(async res => {
          this.paymentDetails = res.data.payments;
          if (this.paymentDetails.length === 0) {
            await this.$router.push({name: 'marketplace-account-payment-add'});
          }
          this.paymentDetailsLoading = false;
        })
        .catch(() => {
          this.paymentDetailsLoading = false;
          this.$snotify.error(t('Error loading your payment details'));
        });
    },
    async handleMakePaymentDefault(paymentDetail) {
      this.paymentDetailsLoading = true;
      await marketplaceApi.updateCurrentUserPaymentDetail({...paymentDetail, defaultPayment: true})
        .then(async() => {
          await this.getPaymentDetails();
          this.paymentDetailsLoading = false;
          this.$snotify.clear();
          this.$snotify.success(t('Default payment method updated'));
        })
        .catch(() => {
          this.paymentDetailsLoading = false;
          this.$snotify.error(t('Error occurred while making address default'));
        });
    },
    async handleRemovePaymentDetail() {
      this.paymentDetailsLoading = true;
      await marketplaceApi.removeCurrentUserPaymentDetail(this.paymentDetailIdToRemove)
        .then(async() => {
          this.paymentDetailIdToRemove = null;
          this.showRemovePaymentDetailModal = false;
          await this.getPaymentDetails();
          this.paymentDetailsLoading = false;
          this.showRemovePaymentDetailModal = false;
          this.$snotify.clear();
          this.$snotify.success(t('Payment method removed'));
        })
        .catch(() => {
          this.paymentDetailIdToRemove = null;
          this.showRemovePaymentDetailModal = false;
          this.paymentDetailsLoading = false;
          this.$snotify.error(t('Error occurred while removing the payment method'));
        });
    },
    handleCancelRemovePaymentDetail() {
      this.showRemovePaymentDetailModal = false;
      this.paymentDetailIdToRemove = null;
    },
    openRemovePaymentDetailModal(paymentDetailId) {
      this.showRemovePaymentDetailModal = true;
      this.paymentDetailIdToRemove = paymentDetailId;
    },
    async handlePaymentDetailUpdate(paymentDetailId) {
      await this.$router.push({
        name: 'marketplace-account-payment-update',
        params: {id: paymentDetailId}
      });
    },
    isCardExpired(paymentDetail) {
      return isCardExpired(paymentDetail);
    }
  }
};
</script>

<style lang="scss" scoped>
.personal-details--footer {
  border-radius: 0 0 1.2rem 1.2rem;
}
</style>
